import { useLayoutEffect, useRef, useState } from "react"

export function useRubberCanvas(referenceHeight = 710, visualInset = 16) {
  const fitAreaRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [visualWidth, setVisualWidth] = useState(390)

  useLayoutEffect(() => {
    const area = fitAreaRef.current
    if (!area) return

    const viewport = area.parentElement
    if (!viewport) return

    let lastVisibleHeight = 0

    const update = () => {
      const areaRect = area.getBoundingClientRect()
      const viewportRect = viewport.getBoundingClientRect()
      const topInset = Math.max(0, areaRect.top - viewportRect.top)
      const bottomInset = Math.max(0, viewportRect.bottom - areaRect.bottom)
      const leftInset = Math.max(0, areaRect.left - viewportRect.left)
      const rightInset = Math.max(0, viewportRect.right - areaRect.right)

      const viewportWidth = window.visualViewport?.width || window.innerWidth || viewportRect.width
      const measuredViewportHeight = window.visualViewport?.height || window.innerHeight || viewportRect.height
      const activeElement = document.activeElement
      const keyboardInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement

      if (!keyboardInputFocused || measuredViewportHeight > lastVisibleHeight) {
        lastVisibleHeight = measuredViewportHeight
      }

      const viewportHeight = keyboardInputFocused && lastVisibleHeight
        ? Math.max(lastVisibleHeight, measuredViewportHeight)
        : measuredViewportHeight

      const width = Math.max(0, Math.min(area.clientWidth, viewportWidth - leftInset - rightInset))
      const height = Math.max(0, Math.min(area.clientHeight, viewportHeight - topInset - bottomInset))
      if (!width || !height) return

      const nextScale = Math.min(1, height / referenceHeight)
      const nextCanvasWidth = width / nextScale
      const nextCanvasInset = visualInset / nextScale

      // Publish the exact rubber-canvas geometry on the owning screen so overlays
      // that are siblings of the body canvas can render in the same coordinate space.
      // This keeps Main/Admin sheets aligned with the page while preserving a real
      // 16px visual edge inset after vertical scaling.
      viewport.style.setProperty("--bbf-rubber-scale", String(nextScale))
      viewport.style.setProperty("--bbf-rubber-visual-width", `${width}px`)
      viewport.style.setProperty("--bbf-rubber-canvas-width", `${nextCanvasWidth}px`)
      viewport.style.setProperty("--bbf-rubber-canvas-inset", `${nextCanvasInset}px`)
      viewport.style.setProperty("--bbf-rubber-reference-height", `${referenceHeight}px`)

      setVisualWidth((current) => Math.abs(current - width) < 0.5 ? current : width)
      setScale((current) => Math.abs(current - nextScale) < 0.001 ? current : nextScale)
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(area)
    window.addEventListener("resize", update)
    window.visualViewport?.addEventListener("resize", update)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", update)
      window.visualViewport?.removeEventListener("resize", update)
      for (const name of [
        "--bbf-rubber-scale",
        "--bbf-rubber-visual-width",
        "--bbf-rubber-canvas-width",
        "--bbf-rubber-canvas-inset",
        "--bbf-rubber-reference-height",
      ]) viewport.style.removeProperty(name)
    }
  }, [referenceHeight, visualInset])

  const canvasWidth = visualWidth / scale
  const canvasInset = visualInset / scale

  return {
    fitAreaRef,
    scale,
    visualWidth,
    canvasWidth,
    canvasInset,
    referenceHeight,
  }
}
